import React from "react";
import { Link } from "react-router-dom";
import "./Healthcare.css";
import { navigateToHomeSection } from '../utils/navigation';

const navLinks = [
  { label: "Services", href: "/#services" },
  { label: "Products", href: "/#products" },
  { label: "Industries", href: "/#industries" },
  { label: "Partners", href: "/#clients" },
  { label: "About", href: "/#about" },
  { label: "How We Work", href: "/#how-we-work" },
  { label: "Case Studies", href: "/#case-studies" },
  { label: "Contact Us", href: "/#contact" },
];
const footerLinks = [
  { label: "Services", href: "/#services" },
  { label: "Products", href: "/#products" },
  { label: "Industries", href: "/#industries" },
  { label: "Partners", href: "/#clients" },
  { label: "About", href: "/#about" },
  { label: "How We Work", href: "/#how-we-work" },
  { label: "Case Studies", href: "/#case-studies" },
  { label: "Contact Us", href: "/#contact" },
];

const metaCards = [
  { label: "SECTOR", value: "Healthcare" },
  { label: "FOCUS", value: "Risk · Ops · Customer" },
  { label: "MATURITY", value: "Production-grade" },
  { label: "COVERAGE", value: "EU & UK" },
];

const plays = [
  { id: "01", title: "Clinical Data Platforms", desc: "Unified platforms integrating EHR, lab, imaging and operational data." },
  { id: "02", title: "Patient Pathway Analytics", desc: "Optimise pathways, reduce LoS and improve outcomes." },
  { id: "03", title: "Operational Forecasting", desc: "Demand and capacity forecasting for elective and urgent care." },
  { id: "04", title: "Clinical NLP & Coding", desc: "Extract structured data from clinical notes and letters." },
  { id: "05", title: "Population Health", desc: "Risk stratification and proactive care models." },
  { id: "06", title: "Clinical Safety & Governance", desc: "DCB0129/0160 compliant delivery and assurance." },
];

const phases = [
  { phase: "PHASE 01", title: "Co-Design", desc: "Engage clinicians, ops and IG from day one.", align: "left" },
  { phase: "PHASE 02", title: "Prove", desc: "Demonstrate clinical and operational value on a defined cohort.", align: "right" },
  { phase: "PHASE 03", title: "Deploy", desc: "Integrate into clinical and operational workflows safely.", align: "left" },
  { phase: "PHASE 04", title: "Sustain", desc: "Monitor outcomes, drift and benefits realisation.", align: "right" },
];

const outcomes = [
  { num: "/ 01", text: "Improved patient outcomes through earlier, better-informed decisions" },
  { num: "/ 02", text: "Reduced length of stay and improved bed flow" },
  { num: "/ 03", text: "Operational savings reinvested in frontline care" },
  { num: "/ 04", text: "Robust clinical safety and information governance" },
];

const otherSectors = [
  { name: "Energy & Utilities", desc: "AI for the energy transition", img: "energy", link: "/energy-utilities" },
  { name: "Financial Services", desc: "AI for banking, lending and insurance", img: "financial", link: "/financial-services" },
  { name: "Education", desc: "AI for higher education and learning", img: "education", link: "/education" },
];

export default function Healthcare() {
  return (
    <div className="hc-root">

      {/* ── HERO  ── */}
      <section className="hc-hero">
        <nav className="hc-nav">
          <div className="hc-nav-inner">
            <div className="hc-logo">
              <span className="hc-logo-aiv">AIV</span><span className="hc-logo-ion">ION</span>
            </div>
            <ul className="hc-nav-links">
              {navLinks.map((l) => <li key={l.label}><a href={l.href} onClick={(e)=>{e.preventDefault(); navigateToHomeSection(l.href);}}>{l.label}</a></li>)}
            </ul>
            <a href="/#contact" className="hc-contact-btn" onClick={(e)=>{e.preventDefault(); navigateToHomeSection('/#contact');}}>CONTACT US</a>
          </div>
        </nav>

        <div className="hc-hero-content">
          <p className="hc-hero-breadcrumb">&#8592; INDUSTRIES</p>
          <div className="hc-hero-sector">
            <span className="hc-sector-icon">&#127974;</span>
            <span>SECTOR FOCUS</span>
          </div>
          <h1 className="hc-hero-title">Healthcare</h1>
          <p className="hc-hero-sub">Trustworthy AI for clinical and operational excellence</p>
        </div>
      </section>

      {/* ── META CARD ROW (overlapping hero/white) ── */}
      <div className="hc-meta-wrap">
        <div className="hc-meta-row">
          {metaCards.map((m) => (
            <div key={m.label} className="hc-meta-card">
              <p className="hc-meta-label">{m.label}</p>
              <p className="hc-meta-value">{m.value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── OPPORTUNITY SECTION ── */}
      <section className="hc-opportunity">
        <div className="hc-opportunity-inner">
          <div className="hc-opp-left">
            <p className="hc-eyebrow">THE OPPORTUNITY</p>
            <h2 className="hc-opp-title">Where AI is changing Healthcare.</h2>
            <span className="hc-opp-pill">&#9679; AIVION sector practice</span>
          </div>
          <div className="hc-opp-right">
            <p className="hc-dropcap-para">
              <span className="hc-dropcap">A</span>VION supports NHS trusts, integrated care systems, private providers and life-sciences organisations in applying AI to improve patient outcomes and the productivity of clinical services. We work within strict information-governance and clinical-safety frameworks (DCB0129/0160, DSPT, GDPR), bringing together data engineering, clinical analytics and applied AI to deliver measurable change.
            </p>
            <blockquote className="hc-quote">
              <span className="hc-quote-mark">&#8220;</span>
              <p>Trustworthy AI for clinical and operational excellence</p>
              <cite>AIVION — Healthcare PRACTICE</cite>
            </blockquote>
          </div>
        </div>
      </section>

      {/* ── SIX HIGH-IMPACT PLAYS ── */}
      <section className="hc-plays">
        <div className="hc-plays-inner">
          <div className="hc-plays-header">
            <div>
              <p className="hc-eyebrow">WHERE WE WORK</p>
              <h2 className="hc-plays-title">Six high-impact plays</h2>
            </div>
            <span className="hc-plays-count">06 AREAS</span>
          </div>
          <div className="hc-plays-list">
            {plays.map((p) => (
              <div key={p.id} className="hc-play-row">
                <span className="hc-play-num">{p.id}</span>
                <h4 className="hc-play-title">{p.title}</h4>
                <p className="hc-play-desc">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── ENGAGEMENT MODEL — ZIGZAG PHASES ── */}
      <section className="hc-phases">
        <div className="hc-phases-inner">
          <p className="hc-eyebrow hc-center">ENGAGEMENT MODEL</p>
          <h2 className="hc-phases-title hc-center">From assessment to lasting capability</h2>
          <div className="hc-phases-track">
            {phases.map((ph) => (
              <div key={ph.phase} className={`hc-phase-row hc-phase-${ph.align}`}>
                <div className="hc-phase-card">
                  <p className="hc-phase-label">{ph.phase}</p>
                  <h4 className="hc-phase-name">{ph.title}</h4>
                  <p className="hc-phase-desc">{ph.desc}</p>
                </div>
                <span className="hc-phase-dot" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── OUTCOMES (dark) ── */}
      <section className="hc-outcomes">
        <div className="hc-outcomes-inner">
          <p className="hc-eyebrow">THE IMPACT</p>
          <h2 className="hc-outcomes-title">Outcomes our Healthcare clients see.</h2>
          <div className="hc-outcomes-grid">
            {outcomes.map((o) => (
              <div key={o.num} className="hc-outcome-cell">
                <span className="hc-outcome-num">{o.num}</span>
                <p>{o.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── OTHER SECTORS ── */}
      <section className="hc-other-sectors">
        <div className="hc-other-inner">
          <div className="hc-other-header">
            <div>
              <p className="hc-eyebrow">OTHER SECTORS</p>
              <h2 className="hc-other-title">Across the industries we serve</h2>
            </div>
            <Link to="/industries" className="hc-other-all">All industries &#8594;</Link>
          </div>
          <div className="hc-other-grid">
            {otherSectors.map((s) => (
              <div key={s.name} className={`hc-sector-card hc-img-${s.img}`}>
                <div className="hc-sector-img" />
                <div className="hc-sector-info">
                  <p className="hc-sector-tag">INDUSTRY</p>
                  <h4>{s.name}</h4>
                  <p className="hc-sector-desc">{s.desc}</p>
                  <Link to={s.link} className="hc-sector-explore">EXPLORE &#8594;</Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA BANNER (dark) ── */}
      <section className="hc-cta">
        <div className="hc-cta-inner">
          <div className="hc-cta-left">
            <p className="hc-eyebrow">START THE CONVERSATION</p>
            <h2 className="hc-cta-title">Bring AIVION's Healthcare expertise to your team.</h2>
            <p className="hc-cta-desc">
              A focused 30-minute briefing with one of our sector leads — exploring the highest-value AI plays for
              your business and how to get there safely.
            </p>
          </div>
          <div className="hc-cta-right">
            <a href="/#contact" className="hc-cta-btn-solid" onClick={(e)=>{e.preventDefault(); navigateToHomeSection('/#contact');}}>REQUEST A SECTOR BRIEFING &#8594;</a>
            <button className="hc-cta-btn-outline" onClick={(e)=>{e.preventDefault(); navigateToHomeSection('/#case-studies');}}>SEE SECTOR CASE STUDIES</button>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="hc-footer">
        <div className="hc-footer-inner">
          <div className="hc-footer-brand">
            <div className="hc-footer-logo">
              <span className="hc-logo-aiv">AIV</span><span className="hc-logo-ion">ION</span>
            </div>
            <p>Delivering meaningful outcomes through intelligent AI solutions.</p>
            <div className="hc-footer-icons">
              <span className="hc-icon">in</span>
              <span className="hc-icon">&#9993;</span>
            </div>
          </div>
          <div className="hc-footer-col">
            <p className="hc-footer-heading">QUICK LINKS</p>
            <div className="footer-quick-links-grid">
              {footerLinks.map((l) => <a key={l.label} href={l.href} className="hc-footer-link" onClick={(e)=>{e.preventDefault(); navigateToHomeSection(l.href);}}>{l.label}</a>)}
            </div>
          </div>
        </div>
        <div className="hc-footer-bottom">
          <p>&#169; 2026 AIVION. All rights reserved.</p>
          
        </div>
      </footer>

    </div>
  );
}
